import Footer from '/web/shared/aifans/Footer'

import { Title, Meta } from '@solidjs/meta'

export default function DmcaPolicy() {
  return (
    <>
      <Title>CosplayFans - DMCA Policy | Copyright Protection & Reporting</Title>
      <Meta
        name="og:title"
        content="CosplayFans - DMCA Policy | Copyright Protection & Reporting"
      />
      <Meta
        name="description"
        content="Read CosplayFans.ai's DMCA Policy to learn how we handle copyright issues, report infringements, and protect original cosplay content and intellectual property."
      />
      <Meta
        name="og:description"
        content="Read CosplayFans.ai's DMCA Policy to learn how we handle copyright issues, report infringements, and protect original cosplay content and intellectual property."
      />

      <section
        class="mx-auto max-w-[960px]"
        style={{ padding: '32px clamp(16px, 3vw, 40px) 56px' }}
      >
        {/* Hero */}
        <div class="py-6 pb-8 text-center">
          <div class="mb-[14px] inline-flex items-center gap-2 rounded-full border border-[rgba(138,43,226,0.3)] bg-[rgba(138,43,226,0.12)] px-[14px] py-[6px] font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]">
            <span class="h-[6px] w-[6px] rounded-full bg-[var(--cf-cyan)] shadow-[0_0_8px_var(--cf-cyan)]" />
            Legal
          </div>
          <h1 class="mb-[14px] font-bebas text-[clamp(36px,5.5vw,64px)] leading-none tracking-[1px] text-white">
            DMCA Policy
          </h1>
          <p class="mx-auto max-w-[600px] font-rajdhani text-[15px] font-medium leading-[1.5] text-[var(--cf-muted)]">
            Please read this document carefully.
          </p>
        </div>

        {/* Content body */}
        <div
          class="legal-content overflow-hidden rounded-[20px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.02)]"
          style={{ padding: 'clamp(24px, 4vw, 48px)' }}
        >
          <p class="font-rajdhani text-[13px] font-semibold text-[var(--cf-muted)]">
            Date of Revision: [ 01/01/2024 ]
          </p>

          <p class="mt-5 font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
            CosplayFans.ai is committed to respecting the intellectual property rights of others and
            is devoted to compliance with the Digital Millennium Copyright Act (DMCA) and applicable
            copyright legislation. Our DMCA Policy outlines the measures we implement to respond to
            claims of copyright infringement and offers instructions for contacting us if you believe
            your copyrighted work has been used on our platform without the necessary permission.
          </p>

          {/* Section 1 */}
          <div class="mt-8">
            <div class="mb-[14px] flex items-center gap-[14px]">
              <div
                class="grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-[10px] font-bebas text-[14px] tracking-[0.5px] text-white shadow-[0_4px_14px_rgba(138,43,226,0.3)]"
                style={{ background: 'var(--cf-grad-brand)' }}
              >
                01
              </div>
              <h3 class="font-bebas text-[22px] leading-[1.15] tracking-[0.8px] text-white">
                Reporting Copyright Infringement
              </h3>
            </div>

            <div class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
              <p class="mb-3">
                Should you, in earnest, suspect that content made available through cosplay-fans.com
                (the &quot;App&quot;) violates your copyright, you or your designated representative
                may send us a formal request for the removal of the content or to restrict access to
                it. Kindly include the following details in your written notification:
              </p>
              <div class="my-[18px] ml-[52px] border-l-2 border-[rgba(138,43,226,0.3)] pl-[18px]">
                <p class="mb-3">
                  a. The signature, either electronic or physical, of the copyright owner or an
                  individual authorized to represent them;
                </p>
                <p class="mb-3">
                  b. A detailed description of the copyrighted work you believe has been infringed,
                  along with enough details for us to identify and locate the work;
                </p>
                <p class="mb-3">
                  c. Your contact information, including address, telephone number, and email address;
                </p>
                <p class="mb-3">
                  d. A declaration from you indicating a bona fide belief that the contentious usage of
                  the material was not approved by the copyright owner, their agent, or by law;
                </p>
                <p class="mb-3">
                  e. A formal statement by you, under the risk of perjury, affirming that the
                  information provided in your notification is accurate and that you are the copyright
                  owner or are duly authorized to act on their behalf.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div class="mt-8">
            <div class="mb-[14px] flex items-center gap-[14px]">
              <div
                class="grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-[10px] font-bebas text-[14px] tracking-[0.5px] text-white shadow-[0_4px_14px_rgba(138,43,226,0.3)]"
                style={{ background: 'var(--cf-grad-brand)' }}
              >
                02
              </div>
              <h3 class="font-bebas text-[22px] leading-[1.15] tracking-[0.8px] text-white">
                DMCA Notices Response
              </h3>
            </div>

            <div class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
              <p class="mb-3">
                Upon receipt of a fully detailed infringement notification, we will proceed as
                follows:
              </p>
              <div class="my-[18px] ml-[52px] border-l-2 border-[rgba(138,43,226,0.3)] pl-[18px]">
                <p class="mb-3">
                  a. Assess and verify that the provided documents comply with the requirements set by
                  the DMCA;
                </p>
                <p class="mb-3">
                  b. Initiate appropriate initial measures to address the reported infringement within
                  1-3 days of receiving such notification, which may include, among other actions,
                  blocking access to the implicated links;
                </p>
                <p class="mb-3">
                  c. Contact the party accused of infringement, requesting their response and
                  submission of any opposing evidence.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div class="mt-8">
            <div class="mb-[14px] flex items-center gap-[14px]">
              <div
                class="grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-[10px] font-bebas text-[14px] tracking-[0.5px] text-white shadow-[0_4px_14px_rgba(138,43,226,0.3)]"
                style={{ background: 'var(--cf-grad-brand)' }}
              >
                03
              </div>
              <h3 class="font-bebas text-[22px] leading-[1.15] tracking-[0.8px] text-white">
                Counter Notification
              </h3>
            </div>

            <div class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
              <p class="mb-3">
                Should you sincerely think that a copyright infringement notice has been mistakenly
                issued against you, you are welcome to submit a counter-notice to us. Upon doing so,
                we will inform the claimant of your counter-claim and pause the removal process for
                10-14 days, during which time we will restore your content unless the copyright owner
                commences legal proceedings against you within that period.
              </p>
            </div>
          </div>

          {/* Section 4 */}
          <div class="mt-8">
            <div class="mb-[14px] flex items-center gap-[14px]">
              <div
                class="grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-[10px] font-bebas text-[14px] tracking-[0.5px] text-white shadow-[0_4px_14px_rgba(138,43,226,0.3)]"
                style={{ background: 'var(--cf-grad-brand)' }}
              >
                04
              </div>
              <h3 class="font-bebas text-[22px] leading-[1.15] tracking-[0.8px] text-white">
                Contact Information
              </h3>
            </div>

            <div class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
              <p class="mb-3">
                Please direct all notices and counter-notices to our email address at
                support@cosplay-fans.com. We are dedicated to resolving issues promptly and providing
                a satisfactory experience for all users.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div class="mt-8">
            <div class="mb-[14px] flex items-center gap-[14px]">
              <div
                class="grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-[10px] font-bebas text-[14px] tracking-[0.5px] text-white shadow-[0_4px_14px_rgba(138,43,226,0.3)]"
                style={{ background: 'var(--cf-grad-brand)' }}
              >
                05
              </div>
              <h3 class="font-bebas text-[22px] leading-[1.15] tracking-[0.8px] text-white">
                Termination
              </h3>
            </div>

            <div class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]">
              <p class="mb-3">
                We reserve the authority to suspend or terminate access to the App for anyone
                believed to be involved in the aforementioned infringement activities.
              </p>
            </div>
          </div>
        </div>

        {/* CTA footer */}
        <div class="mt-9 border-t border-[rgba(255,255,255,0.06)] pt-7 text-center">
          <p class="mb-4 font-rajdhani text-[14px] font-semibold text-[var(--cf-muted)]">
            Have questions about this policy?
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <a
              href="/contact-us"
              class="inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-rajdhani text-[14px] font-extrabold uppercase tracking-[0.8px] text-white transition-all duration-200 hover:translate-y-[-2px]"
              style={{
                background:
                  'linear-gradient(135deg, var(--cf-primary) 0%, #6a1fb8 50%, var(--cf-primary) 100%)',
                'background-size': '200% auto',
                'box-shadow': '0 6px 24px rgba(138,43,226,0.45)',
              }}
            >
              Contact Support
            </a>
            <a
              href="/faq"
              class="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[rgba(255,255,255,0.12)] bg-transparent px-6 font-rajdhani text-[14px] font-extrabold uppercase tracking-[0.8px] text-white transition-colors hover:border-[var(--cf-cyan)] hover:text-[var(--cf-cyan)]"
            >
              FAQ
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
